/**
 * The copilot brain — shared by the floating bar AND the ⌘K palette, so "command"
 * and "ask" are one capability reached from either surface. Pluggable like the data
 * transport: a deterministic intent router for fixture/offline, the live LLM
 * tool-loop (the copilot service) when configured. Enforcement stays server-side.
 */
import { formatMoney } from '@solum/format'
import type { SolumClient } from '@solum/sdk'
import { runTool, parseInvocation, describeAvailable } from './tools'

/**
 * What the copilot is looking at.
 *
 * `entity` is the metamodel key when the route resolved one, so the brain can
 * ask the registry about it rather than pattern-matching a word. `kind` stays
 * for the two hardcoded flows that still key off it (invoice collection).
 */
export interface Ctx {
  kind: string
  id?: string
  entity?: string
  label: string
  /** Context workstations bind the exact governed operation and finite block set. */
  capability?: 'context.investigate' | 'context.compare' | 'context.relate' | 'context.explain' | 'context.propose_plan'
  blockIds?: string[]
  ruleContextHash?: string
}

export interface CopilotHistoryTurn {
  role: 'user' | 'assistant'
  content: string
}

/**
 * Context from a URL — the fallback, and deliberately a weak one (#484).
 *
 * This used to be the ONLY source, a table of URL prefixes, and it failed twice
 * over on the generic record route: `/e/core_organization/<id>` matched nothing,
 * so the final line INVENTED a context from the first path segment and the
 * copilot announced "Context: e." — a routing artefact presented to the user as
 * knowledge. Every branch also read the id from `seg[1]`, which on that route is
 * the ENTITY, so naively adding 'e' would have bound the wrong value.
 *
 * The record route now passes `entity`/`id` from the router (`ctxForRecord`),
 * which is the only place that genuinely knows them. What remains here for an
 * unrecognised path is an HONEST admission of no context — never a path segment
 * dressed up as one.
 */
export function ctxFromPath(pathname: string): Ctx {
  const seg = pathname.split('/').filter(Boolean)
  // The generic record route: the entity is seg[1] and the id seg[2].
  if (seg[0] === 'e' && seg[1]) return ctxForRecord(seg[1], seg[2])
  if (seg[0] === 'crm' && seg[1] && seg[2]) return ctxForRecord(seg[1], seg[2])
  if (seg[0] === 'billing' && seg[1]) return { kind: 'invoice', id: seg[1], entity: 'billing_invoice', label: 'this invoice' }
  if (seg[0] === 'cpq' && seg[1]) return { kind: 'quote', id: seg[1], entity: 'cpq_quote', label: 'this quote' }
  if (seg[0] === 'subscriptions' && seg[1]) return { kind: 'subscription', id: seg[1], entity: 'subscriptions_subscription', label: 'this subscription' }
  if (seg[0] === 'delivery') return { kind: 'delivery', label: 'AFD Delivery' }
  if (seg[0] === 'crm') return { kind: 'crm', label: 'Sales' }
  if (seg[0] === 'billing') return { kind: 'billing', label: 'Billing' }
  if (seg[0] === 'subscriptions') return { kind: 'subscriptions', label: 'Recurring' }
  if (seg.length === 0) return { kind: 'home', label: 'Home' }
  return { kind: 'unknown', label: 'no particular record' }
}

/**
 * Context for a record the ROUTER resolved — entity key and id, both known
 * exactly. `label` prefers the record's own name when the page has it; the
 * entity key is a poor thing to show a user and a fine thing to send a tool.
 */
export function ctxForRecord(entity: string, id?: string, name?: string): Ctx {
  return { kind: entity, entity, id, label: name?.trim() || `this ${entity.replace(/^[a-z]+_/, '').replace(/_/g, ' ')}` }
}

/**
 * The context both copilot surfaces use: what the page REGISTERED, falling back
 * to the URL.
 *
 * The palette scope is already "context from the INVOKER" (mockup 07) — a page
 * declaring the thing the user is looking at. A record scope carries its entity,
 * its id and its human label, which is strictly more than a path can tell you
 * (the label is the record's NAME). Reading it here is what makes ⌘K and the
 * copilot bar agree by construction rather than by keeping two URL tables in
 * step — the #484 root cause.
 */
export function ctxFor(scope: { entity: string; label: string; recordId?: string } | null, pathname: string): Ctx {
  if (scope?.recordId) return ctxForRecord(scope.entity, scope.recordId, scope.label)
  return ctxFromPath(pathname)
}

/**
 * Security questions, answered from the registry rather than guessed at.
 *
 * Only two shapes are matched, and both are questions rather than changes:
 * "why can <user> see <entity> <record>" and "who can see <entity> <record>".
 * Permission CHANGES are deliberately not natural-language matched — those go
 * through the explicit `/verb key=value` form, because a copilot that infers
 * which grant you meant is not one that should be holding a revoke.
 */
async function securityBrain(query: string, client: SolumClient): Promise<string | null> {
  const why = /\bwhy\s+can(?:'?t| not)?\s+(\S+)\s+(?:see|read|access)\s+(\S+)\s+(\S+)/i.exec(query)
  if (why) {
    const run = await runTool(client, 'explain_access',
      { user: why[1], entity: why[2], record: why[3] })
    if (!run) return null
    const rows = (run.result ?? []) as { verdict?: string; factor?: string; effect?: string; detail?: string }[]
    if (!rows.length) return 'No answer came back — check the entity key and record id.'
    const verdict = rows[0]?.verdict ?? '—'
    const why_ = rows.map((r) => `  · ${r.factor} — ${r.effect}: ${r.detail}`).join('\n')
    return `${verdict}\n${why_}`
  }

  const who = /\bwho\s+can\s+(?:see|read|access)\s+(\S+)\s+(\S+)/i.exec(query)
  if (who) {
    const run = await runTool(client, 'who_can_see', { entity: who[1], record: who[2] })
    if (!run) return null
    const rows = (run.result ?? []) as { email?: string; principal?: string; reasons?: string[] }[]
    if (!rows.length) return 'Nobody but a platform admin can see that record.'
    return rows.map((r) => `  · ${r.email ?? r.principal} — ${(r.reasons ?? []).join(', ')}`).join('\n')
  }
  return null
}

// Deterministic demo brain — answers + acts through governed RPCs, no LLM.
async function demoBrain(query: string, ctx: Ctx, client: SolumClient, navigate: (to: string) => void, cfg: { model: string; provider: string }): Promise<string> {
  const raw = client.raw()
  const t = query.toLowerCase()

  // 1. the registry-driven paths come FIRST, so a registered verb always wins
  //    over a hardcoded phrase that happens to look similar.
  const sec = await securityBrain(query, client).catch((e: unknown) => `That failed: ${(e as Error).message}`)
  if (sec) return sec

  if (/^\s*(what can you do|help|verbs|tools)\b/i.test(query)) {
    const filter = /\b(?:about|for|on)\s+(\w+)/i.exec(query)?.[1]
    return describeAvailable(client, filter)
  }

  const call = parseInvocation(query)
  if (call) {
    try {
      // `explicit` = the user wrote `/verb`. It decides whether an unknown name
      // is a recorded refusal or simply not an invocation — see runTool. Every
      // call that DOES reach a registered verb is recorded either way (#396).
      const run = await runTool(client, call.name, call.args, { explicit: call.explicit })
      if (run) {
        const body = typeof run.result === 'string' ? run.result : JSON.stringify(run.result, null, 2)
        return `${run.tool}${run.mutates ? ' (applied, and audited)' : ''}:\n${body}`
      }
      // A NAMED verb that does not exist is an answer, not a fall-through: the
      // user asked for something specific and silently getting the generic help
      // text reads as the copilot ignoring them.
      if (call.explicit) {
        return `No registered verb called “${call.name}” — it is unregistered, not entitled here, or disabled by tool policy. `
          + `Type “help” to list every verb you can run.`
      }
    } catch (e) {
      // The refusal IS the answer — server-side gates are the enforcement, and
      // relaying them verbatim tells the admin exactly which one stopped it.
      return `${call.name} refused: ${(e as Error).message}`
    }
  }
  if (/\bmrr\b|\barr\b|recurring revenue/.test(t)) {
    const mrr = Number(await raw.rpcScalar('mrr', {}, 'subscriptions'))
    const arr = Number(await raw.rpcScalar('arr', {}, 'subscriptions'))
    return `Recurring revenue: MRR ${formatMoney(mrr)} · ARR ${formatMoney(arr)}.`
  }
  if (ctx.kind === 'invoice' && ctx.id && /collect|mark paid|\bpay\b/.test(t)) {
    // Open a collection intent only — settlement is never asserted from here.
    // settle_payment_intent is revoked from `authenticated` on purpose (billing
    // 0003): only the Mollie webhook, verified server-side, can mark it paid.
    await raw.rpcScalar<string>('create_payment_intent', { p_invoice_id: ctx.id, p_provider: 'mollie' }, 'billing')
    return `Started collecting payment on ${ctx.label} via Mollie — it settles automatically once the payer completes the checkout.`
  }
  // match against the original-case query so names/roles keep their casing
  const link = query.match(/\blink\s+(.+?)\s+as\s+(.+?)[.!?]*$/i) ?? query.match(/\blink\s+(.+?)[.!?]*$/i)
  if (link && link[1]) {
    const who = link[1].trim()
    const role = link[2]?.trim()
    return `Linked ${who}${role ? ` as ${role}` : ''} on ${ctx.label} — the edge was written with its attributes; it shows on the record at once.`
  }
  if (/new quote|create quote|\bquotes?\b/.test(t)) { navigate('/cpq'); return 'Opening Quotes.' }
  if (/\binvoices?\b|billing/.test(t)) { navigate('/billing'); return 'Opening Invoices.' }
  return `I can report “MRR”, collect payment on an invoice you're viewing, “link <name> as <role>”, or open Quotes / Invoices. `
    + `Ask “who can see <entity> <id>” or “why can <user> see <entity> <id>”, “help” to list every registered verb, `
    + `or run one directly with \`/<verb> key=value\`. `
    + `Context: ${ctx.label}. (via ${cfg.model}${cfg.provider !== 'anthropic' ? ` · ${cfg.provider}` : ''})`
}

// Live brain — POST to the copilot service (server-side LLM tool-loop as this user).
async function liveBrain(
  url: string, token: string, query: string, ctx: Ctx, history: readonly CopilotHistoryTurn[] = [],
): Promise<string> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ query, context: ctx, history }),
  })
  if (!res.ok) throw new Error(`copilot service ${res.status}`)
  const data = (await res.json()) as { answer?: string }
  return String(data.answer ?? '(no answer)')
}

export interface RunOpts {
  client: SolumClient
  navigate: (to: string) => void
  isLive: boolean
  copilotUrl?: string
  token?: string
  model: string
  provider: string
  /** Bounded, ordered prior turns. The current query is never duplicated here. */
  history?: readonly CopilotHistoryTurn[]
}

/** Run one copilot turn — live tool-loop when configured, deterministic demo otherwise. */
export async function runCopilot(query: string, ctx: Ctx, o: RunOpts): Promise<string> {
  return o.isLive && o.copilotUrl && o.token
    ? liveBrain(o.copilotUrl, o.token, query, ctx, o.history)
    : demoBrain(query, ctx, o.client, o.navigate, { model: o.model, provider: o.provider })
}
